import { useMemo, useState } from 'react';
import {
  CHANNELS, FEED_DEPARTMENTS, OUTCOMES, labelOf, useClientCommunications, useCommunicationVersions, asEdits,
  type Channel, type Communication, type Feed,
} from '../../api/communications.ts';
import { EarlierVersions, EditedMarker } from '../EarlierVersions.tsx';
import { RecordEdit } from '../RecordEdit.ts';
import { secondary } from '../contacts/Sheet.tsx';
import { formatDate } from '../../dates.ts';
import { CommunicationForm, FollowUpForm, LogButtons, WithdrawForm } from './CommunicationForms.tsx';
import { callFollowUpLine } from '../../tasks/taskView.ts';

/**
 * A client's communications (138; Arthur, 20 October): ONE chronological feed, newest first, each entry tagged with
 * its department. Department buttons, Finance | Social Work | Legal | Compliance | All, show one department's
 * entries or every one (139): each reader starts on their own department, tiers 1-2 and anyone whose department has
 * no button on All, the Clio-like view; the view is the same for everyone. Then filters for how, outcome and
 * emergencies only. A quick add; each entry shows its current version once, "Edited" and its earlier versions as a
 * visit note does (125), and, for whoever may, Correct, Withdraw and a follow-up task in one click. An emergency
 * says so in words on a band, never by colour alone.
 */
export function CommunicationsFeed({ clientId }: { clientId: string }) {
  const [withdrawn, setWithdrawn] = useState(false);
  const q = useClientCommunications(clientId, withdrawn);
  const [adding, setAdding] = useState<Channel | null>(null);
  const [deptPicked, setDept] = useState<string | null>(null);
  const dept = deptPicked ?? q.data?.default_feed_department ?? 'All';
  const [channel, setChannel] = useState<string | null>(null);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [emergencyOnly, setEmergencyOnly] = useState(false);
  const entries = q.data?.entries ?? [];
  const shown = useMemo(() => entries.filter((e) => (dept === 'All' || e.department === dept) && (!channel || e.channel === channel)
    && (!outcome || e.outcome === outcome) && (!emergencyOnly || e.is_emergency)), [entries, dept, channel, outcome, emergencyOnly]);

  if (q.error && !q.data) return <p role="alert" className="px-4 pb-3 text-warning-fg">{(q.error as Error).message}</p>;
  if (!q.data) return <p className="px-4 pb-3 text-neutral-600">Loading…</p>;

  return (
    <div className="space-y-3 px-4 pb-3">
      <div className="flex flex-wrap items-center gap-2">
        {q.data.may_log && !adding && <LogButtons onPick={setAdding} />}
        {q.data.open_emergencies > 0 && (
          <span className="rounded-lg border border-critical-edge bg-critical-bg px-3 py-2 text-sm font-semibold text-critical-fg">
            {q.data.open_emergencies} emergency {q.data.open_emergencies === 1 ? 'entry' : 'entries'}
          </span>
        )}
      </div>
      {adding && (
        <CommunicationForm key={adding} clientId={clientId} ctx={q.data} channel={adding} onDone={() => setAdding(null)} />
      )}

      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Department">
        {[...FEED_DEPARTMENTS, 'All'].map((d) => <Chip key={d} on={dept === d} onClick={() => setDept(d)}>{d}</Chip>)}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm" role="group" aria-label="Filter the communications">
        <select aria-label="How" value={channel ?? ''} onChange={(e) => setChannel(e.target.value || null)} className={select}>
          <option value="">Any way</option>
          {CHANNELS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select aria-label="Outcome" value={outcome ?? ''} onChange={(e) => setOutcome(e.target.value || null)} className={select}>
          <option value="">Any outcome</option>
          {OUTCOMES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <label className="inline-flex min-h-[44px] items-center gap-2">
          <input type="checkbox" checked={emergencyOnly} onChange={(e) => setEmergencyOnly(e.target.checked)} /> Emergencies only
        </label>
        <label className="inline-flex min-h-[44px] items-center gap-2">
          <input type="checkbox" checked={withdrawn} onChange={(e) => setWithdrawn(e.target.checked)} /> Show withdrawn
        </label>
      </div>

      {entries.length === 0 ? (
        <p className="text-neutral-700">No communications are logged for this client yet.</p>
      ) : shown.length === 0 ? (
        <p className="text-neutral-700">{dept === 'All' ? 'None match these filters.' : `No ${dept} entries match. All shows every department.`}</p>
      ) : (
        <ol className="space-y-2">
          {shown.map((e) => <Entry key={e.id} e={e} feed={q.data!} clientId={clientId} />)}
        </ol>
      )}
    </div>
  );
}

function Entry({ e, feed, clientId }: { e: Communication; feed: Feed; clientId: string }) {
  const [mode, setMode] = useState<'view' | 'edit' | 'withdraw' | 'task'>('view');
  const [sent, setSent] = useState<string | null>(null);
  const edited = e.current_version > 1;
  const versions = useCommunicationVersions(e.id, edited);
  const edits = versions.data ? asEdits(versions.data.versions) : undefined;
  return (
    <li className={`rounded-lg border p-3 ${e.is_emergency && !e.withdrawn ? 'border-critical-edge' : 'border-neutral-300'} bg-panel`}>
      {e.is_emergency && !e.withdrawn && (
        <p className="-mx-3 -mt-3 mb-2 rounded-t-lg bg-critical-bg px-3 py-1 text-sm font-semibold text-critical-fg">Emergency</p>
      )}
      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-neutral-700">
        <span className="font-semibold text-neutral-900">{when(e.occurred_at)}</span>
        <span>{labelOf(CHANNELS, e.channel)}{e.direction !== 'none' ? `, ${e.direction}` : ''}{e.duration_minutes !== null ? `, ${e.duration_minutes} min` : ''}</span>
        {e.who && <span>with {e.who}</span>}
        <span className="rounded bg-tint px-1.5 py-0.5 font-semibold text-brand-text">{e.department}</span>
        <span className="rounded bg-note-bg px-1.5 py-0.5 text-note-fg">{labelOf(OUTCOMES, e.outcome)}</span>
      </p>
      <EntryContent e={e} />
      {(e.contact_phone || e.contact_email) && !e.contact_id && (
        <p className="text-sm text-neutral-700">{[e.contact_phone, e.contact_email].filter(Boolean).join(' · ')}</p>
      )}
      <p className="text-sm text-neutral-600">Logged by {e.recorded_by ?? 'someone not on record'}, {when(e.recorded_at)}{e.source !== 'manual' ? ` (from ${e.source})` : ''}</p>
      {e.follow_up_task_id && (
        <p className="text-sm text-neutral-800">Follow-up task: {callFollowUpLine(e.follow_up_status)}{e.follow_up_due ? `, due ${formatDate(e.follow_up_due)}` : ''}</p>
      )}
      {sent && <p role="status" className="text-sm font-semibold text-neutral-900">{sent}</p>}
      {edited && <EditedMarker edits={edits} />}
      {e.withdrawn && (
        <p className="mt-1 rounded-lg bg-neutral-100 px-3 py-2 text-sm font-semibold text-neutral-800">
          Withdrawn: {e.withdrawal_reason}. It stays on record and counts toward nothing.
        </p>
      )}
      {edited && <EarlierVersions edits={edits} loading={versions.isLoading} error={versions.error as Error | null} />}

      {mode === 'view' && !e.withdrawn && (
        <div className="mt-2 flex flex-wrap gap-2">
          {feed.may_log && !e.follow_up_task_id && (
            <button type="button" className={secondary} onClick={() => setMode('task')}>Make a follow-up task</button>
          )}
          <RecordEdit may={e.may_edit} className={secondary} choices={[
            { key: 'correct', label: 'Correct', onPick: () => setMode('edit') },
            { key: 'withdraw', label: 'Withdraw: logged in error', onPick: () => setMode('withdraw') },
          ]} />
        </div>
      )}
      {mode === 'edit' && <CommunicationForm clientId={clientId} ctx={feed} channel={e.channel} editing={e} onDone={() => setMode('view')} />}
      {mode === 'withdraw' && <WithdrawForm id={e.id} onDone={() => setMode('view')} />}
      {mode === 'task' && <FollowUpForm entry={e} feed={feed} onDone={(said) => { setSent(said ?? null); setMode('view'); }} />}
    </li>
  );
}

/**
 * What an entry says: its summary, and for an email its subject, everyone included and its body exactly as pasted
 * (whitespace kept, folded until opened).
 */
export function EntryContent({ e }: { e: Communication }) {
  return (
    <>
      {e.subject && <p className="mt-1 font-semibold text-neutral-900">{e.subject}</p>}
      {e.participants.length > 0 && (
        <p className="text-sm text-neutral-700">
          {e.participants.map((p) => `${p.kind.toUpperCase()}: ${p.name ? `${p.name} <${p.address}>` : p.address}`).join(', ')}
        </p>
      )}
      {e.summary && <p className="mt-1 whitespace-pre-line text-[0.9375rem] text-neutral-900">{e.summary}</p>}
      {e.body && (
        <details className="mt-1">
          <summary className="min-h-[44px] cursor-pointer py-2 text-sm font-semibold text-brand-text">The email as pasted</summary>
          <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-neutral-100 p-2 font-mono text-sm text-neutral-900">{e.body}</pre>
        </details>
      )}
    </>
  );
}

function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick}
            className={`min-h-[44px] rounded-full border px-3 font-semibold ${on ? 'border-brand bg-brand text-on-brand' : 'border-neutral-400 text-neutral-800'}`}>
      {children}
    </button>
  );
}

/** An instant in the viewer's own zone (timestamptz: converting it is right). */
export const when = (instant: string) => new Date(instant).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
const select = 'min-h-[44px] rounded-lg border border-neutral-400 bg-panel px-2 text-neutral-900';
